/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */
import { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Autocomplete,
  FormControl,
  Button,
  // RadioGroup,
  // FormControlLabel,
  // Radio,
  MenuItem,
  Select,
  InputLabel,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  IconButton,
  Stack,
  Chip,
  Tooltip,
  Link,
  Collapse,
} from '@mui/material';
import { Formik } from 'formik';
import { X } from 'react-feather';
import { differenceInDays, nextMonday, startOfDay } from 'date-fns';
import MODULE from 'modules/common/constants/modules';
import { toast } from 'react-toastify';
import TOAST_TYPES from 'modules/common/constants/toast-types';
import { useDispatch, useSelector } from 'react-redux';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { notificationActions } from 'modules/common/notifications/slice';
import { selectNotification } from 'modules/common/notifications/selectors';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import styles from 'modules/dashboard/components/tab-container/inner-filter/inner-filter.module.scss';
import { selectHotelDropdownList } from 'modules/dashboard/selectors';
import { CustomToggleSwitch } from '../../../../../common/components';
import { formatDateLabel } from '../../../../functions';
import { formatAlertName, formatLabel } from '../functions';
import { CONFIGURE_TYPE, recurrenceOptions, targetDateTypes } from '../constants';
import { alertActions } from '../slice';
import { selectAlertParam } from '../selectors';
import { addAlertValidation } from '../validations/alert-validation';
import AlertDateRangePicker from './date-picker';

/**
 * Component to create alert
 * @param {*} open - open modal
 * @param {*} onClose - function to set when close modal
 * @returns
 */
const ConfigureAlert = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const alertParams = useSelector(selectAlertParam);
  const hotelList = useSelector(selectHotelDropdownList);
  const notification = useSelector(selectNotification);
  const organizationId = useSelector(selectOrganizationId);
  const currentUser = useSelector(selectAuthUser);
  const [alertOptions, setAlertOptions] = useState([]);
  const [alertConfigs, setAlertConfigs] = useState({});
  const [inputValue, setInputValue] = useState('');
  const [isAudit, setIsAudit] = useState('');
  const [maxLead, setMaxLead] = useState();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [dates, setDates] = useState({
    startDate: nextMonday(new Date()).toISOString(),
    endDate: nextMonday(new Date()).toISOString(),
    key: 'selection',
  });

  // close modal and reset notifications
  const modalAction = (action) => {
    if (notification?.isEnabled) dispatch(notificationActions.resetNotification());
    setDates({
      startDate: nextMonday(new Date()).toISOString(),
      endDate: nextMonday(new Date()).toISOString(),
      key: 'selection',
    });
    setIsAudit('');
    onClose(action);
  };

  useEffect(() => {
    setMaxLead(differenceInDays(startOfDay(new Date(dates?.startDate)), new Date()));
  }, [dates]);

  // Triggers when notification state changes
  useEffect(() => {
    // check notification status and toast notification
    if (
      notification?.isEnabled &&
      notification?.type === ERROR_TYPES.SUCCESS &&
      notification?.module !== MODULE.preference
    ) {
      toast(notification?.message, {
        type: TOAST_TYPES.SUCCESS,
      });
      modalAction(false);
    }
  }, [notification]);

  useEffect(() => {
    if (!Array.isArray(alertParams)) return;

    const options = alertParams
      .map((a) => ({
        label: formatAlertName(a.alertName),
        value: a.alertName,
      }))
      .filter((v) => v.value !== 'threshold_percentile_alert');

    const configs = {};
    alertParams.forEach((a) => {
      // Create a shallow copy of alertParams object before adding new property
      configs[a.alertName] = {
        ...a.alertParams,
      };
    });

    setAlertOptions(options);
    setAlertConfigs(configs);
  }, [alertParams]);

  const handleDateChange = (item, alertName) => {
    if (alertName === 'pace_audit_vs_stlp') {
      const singleDate = item?.selection?.startDate || item?.startDate;
      setDates({
        startDate: singleDate,
        endDate: singleDate,
        key: 'selection',
      });
    } else {
      setDates({
        startDate: item?.selection?.startDate,
        endDate: item?.selection?.endDate,
        key: 'selection',
      });
    }
  };

  const onSubmitCreateAlert = async (values) => {
    const valueObj = {
      type: 'data',
      name: values.alertCustomName,
      hotelIds: values.hotelCodes,
      alertProc: values.alertName,
      alertParams: {
        ...values.alertParams,
        ...(values.alertName !== 'pace_audit_vs_stlp' && { dayOfWeekAlignment: values.isAligned }),
      },
      targetDateType: values.targetDateType,
      targetDateStart: new Date(dates.startDate).toISOString(),
      targetDateEnd: new Date(dates.endDate).toISOString(),
      targetDateRecurrence: values.targetDateRecurrence,
      evalLead: values.evalLead,
      author: currentUser?.id,
    };
    dispatch(
      alertActions.createAlert({
        organizationId,
        ...valueObj,
      })
    );
  };
  //
  return (
    <Dialog open={open} onClose={() => modalAction(false)} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">
        <Grid container item flexDirection="row" justifyContent="space-between" xs={12}>
          <Grid item xs={3.5}>
            <Typography variant="h5">Create Alert</Typography>
          </Grid>

          <Grid item xs={3.5}>
            <IconButton
              size="small"
              aria-label="close"
              onClick={() => {
                modalAction(false);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <X />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <Box mt={2}>
          <Formik
            enableReinitialize
            initialValues={{
              alertName: '',
              alertCustomName: '',
              hotelCodes: [],
              targetDateType: 'stay_date',
              targetDateRecurrence: recurrenceOptions[0].value,
              isAligned: false,
              evalLead: [1],
              evalDate: '',
              alertParams: {}, // For dynamic alert config fields
            }}
            validationSchema={addAlertValidation}
            onSubmit={onSubmitCreateAlert}
          >
            {({
              values,
              handleChange,
              setFieldValue,
              handleSubmit,
              isSubmitting,
              touched,
              errors,
              handleBlur,
            }) => {
              useEffect(() => {
                if (!maxLead) return;
                setFieldValue('evalLead', [maxLead], false);
              }, [maxLead]);
              // Current alert config props or empty object
              const currentConfigProps = alertConfigs[values.alertName]?.properties || {};
              return (
                <form noValidate onSubmit={handleSubmit}>
                  {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
                    <Alert mt={2} mb={3} severity={notification?.type}>
                      {notification?.message}
                    </Alert>
                  )}

                  <Grid container spacing={6}>
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        {/* Alert Type Selector */}
                        <Grid item xs={12} mt={4}>
                          <Autocomplete
                            fullWidth
                            options={alertOptions}
                            getOptionLabel={(option) =>
                              typeof option === 'string' ? option : option.label
                            }
                            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                            value={
                              alertOptions.find((opt) => opt.value === values.alertName) || null
                            }
                            onChange={(e, selected) => {
                              setFieldValue('alertName', selected?.value || '');
                              // reset dynamic fields when alert changes
                              setIsAudit(
                                alertConfigs[selected.value]?.title?.includes(
                                  CONFIGURE_TYPE.AUDIT?.toLowerCase()
                                )
                                  ? CONFIGURE_TYPE.AUDIT
                                  : CONFIGURE_TYPE.ALERT
                              );
                              setFieldValue('alertParams', {});

                              const result = Object.fromEntries(
                                Object.entries(alertConfigs[selected.value]?.properties || {})
                                  .slice(1)
                                  .map(([key, config]) => [key, config.default])
                              );

                              setFieldValue('alertParams', result);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Type"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} mt={4}>
                          <TextField
                            name="alertCustomName"
                            value={values.alertCustomName}
                            error={Boolean(touched.alertCustomName && errors.alertCustomName)}
                            helperText={touched.alertCustomName && errors.alertCustomName}
                            fullWidth
                            size="small"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            // my={2}
                            label={`${isAudit} Name`}
                          />
                        </Grid>
                        {/* Hotel Selector */}
                        <Grid item xs={12} mt={4}>
                          <Autocomplete
                            size="small"
                            multiple
                            fullWidth
                            name="hotelCodes"
                            options={hotelList.map((h) => ({
                              label: h.hotel || h.name,
                              value: h.hotelCode,
                            }))}
                            getOptionLabel={(option) =>
                              typeof option === 'string' ? option : option.label
                            }
                            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                            value={
                              hotelList
                                .map((h) => ({ label: h.hotel || h.name, value: h.hotelCode }))
                                .filter((opt) => values.hotelCodes?.includes(opt.value)) ?? []
                            }
                            onChange={(e, selectedOptions) => {
                              // selectedOptions is an array of selected option objects
                              const selectedIds = selectedOptions?.map((opt) => opt.value) || [];
                              setFieldValue('hotelCodes', selectedIds);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Hotels"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                error={Boolean(touched.hotelCodes && errors.hotelCodes)}
                                helperText={touched.hotelCodes && errors.hotelCodes}
                              />
                            )}
                            componentsProps={{
                              paper: {
                                sx: {
                                  width: '100%',
                                  whiteSpace: 'unset',
                                  wordBreak: 'break-all',
                                },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                padding: 0,
                                height: 'fit-content',
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} mt={4}>
                          <Autocomplete
                            fullWidth
                            options={targetDateTypes}
                            getOptionLabel={(option) =>
                              typeof option === 'string' ? option : option.label
                            }
                            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                            value={
                              targetDateTypes.find((opt) => opt.value === values.targetDateType) ||
                              null
                            }
                            onChange={(e, selected) => {
                              setFieldValue('targetDateType', selected?.value || '');
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Target Basis"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                              />
                            )}
                            disableClearable
                          />
                        </Grid>
                        {/* Target Date Range */}
                        <Grid item xs={12} mt={4}>
                          <FormControl fullWidth>
                            <AlertDateRangePicker
                              label="Target Date"
                              handleChange={(item) => handleDateChange(item, values.alertName)}
                              ranges={[dates]}
                              dateValue={formatDateLabel(dates)}
                              cssClassName={styles.calendarElement}
                            />
                          </FormControl>
                        </Grid>
                        {/* Target Date Recurrence */}
                        <Grid item xs={12} mt={4}>
                          <Tooltip title="Set a relative repetition of the target date range chosen">
                            <Autocomplete
                              fullWidth
                              options={recurrenceOptions}
                              getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option.label
                              }
                              isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                              value={
                                recurrenceOptions.find(
                                  (opt) => opt.value === values.targetDateRecurrence
                                ) || null
                              }
                              onChange={(e, selected) => {
                                setFieldValue('targetDateRecurrence', selected?.value || '');
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Target Date Recurrence"
                                  size="small"
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                              disableClearable
                            />
                          </Tooltip>
                        </Grid>
                        {/* Target Date Recurrence Alignment */}
                        {values.alertName !== 'pace_audit_vs_stlp' && (
                          <Grid item xs={12} mt={4}>
                            <CustomToggleSwitch
                              label="Day Of Week Alignment"
                              title={
                                <span>
                                  Whether to match the day-of-week between the current selection and
                                  the historical comparison. Please see the{' '}
                                  <Link
                                    href="https://amicacloud.blob.core.windows.net/pub/prop_users_manual_v3.pdf"
                                    underline="hover"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    User Guide
                                  </Link>{' '}
                                  for further details.
                                </span>
                              }
                              checked={values.isAligned}
                              onChange={(event) => {
                                setFieldValue('isAligned', event.target.checked);
                              }}
                            />
                          </Grid>
                        )}
                        {/* Evaluation Lead Time */}
                        <Grid item xs={12} mt={4}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <TextField
                                type="number"
                                variant="outlined"
                                label="Evaluation Lead Time"
                                placeholder="Enter lead time in days"
                                value={inputValue}
                                onChange={(e) => {
                                  setInputValue(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  if (inputValue !== '' && e.key === 'Enter') {
                                    e.preventDefault(); // prevent Formik submit
                                    setFieldValue('evalLead', [...values.evalLead, inputValue]);
                                    setInputValue('');
                                  }
                                }}
                                onBlur={() => {
                                  const num = Number(inputValue);

                                  if (
                                    Number.isNaN(num) ||
                                    inputValue === '' ||
                                    num < 0 ||
                                    num > maxLead ||
                                    values.evalLead.includes(num) ||
                                    values.evalLead.length >= 4
                                  ) {
                                    setInputValue('');
                                    return;
                                  }

                                  setFieldValue('evalLead', [...values.evalLead, num]);
                                  setInputValue('');
                                }}
                                sx={{ minWidth: 205 }}
                                InputProps={{
                                  inputProps: {
                                    max: maxLead,
                                    min: 0,
                                  },
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                  sx: {
                                    transform: 'translate(14px, -9px) scale(0.75)',
                                    backgroundColor: 'white',
                                    padding: '0 4px',
                                  },
                                }}
                                disabled={values.evalLead?.length === 4}
                              />
                              <IconButton
                                color="primary"
                                onClick={() => {
                                  if (inputValue !== '') {
                                    setFieldValue('evalLead', [...values.evalLead, inputValue]);
                                    setInputValue('');
                                  }
                                }}
                                disabled={values.evalLead?.length === 4}
                              >
                                <AddCircleIcon />
                              </IconButton>
                            </Stack>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {values.evalLead?.map((num, index) => (
                                <Chip
                                  key={`${index.toString()}-${num}`}
                                  label={num}
                                  onDelete={() => {
                                    const updatedList = values?.evalLead?.filter(
                                      (_, i) => i !== index
                                    );
                                    setFieldValue('evalLead', updatedList);
                                  }}
                                  color="default"
                                  sx={{ mb: 1, mr: 1 }}
                                />
                              ))}
                            </Stack>
                          </Stack>
                        </Grid>
                        <Grid container item xs={12} mt={4} direction="row-reverse">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setShowAdvanced((prev) => !prev)}
                            sx={{
                              display: 'block',
                              mt: 2,
                              mb: 2,
                            }}
                            disabled={!values.alertName}
                          >
                            {showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
                          </Button>
                        </Grid>
                        <Collapse in={showAdvanced}>
                          <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                              {/* Dynamic Fields Based on Selected Alert Config */}
                              {Object.entries(currentConfigProps).map(([key, config]) => {
                                if (/_$/.test(key)) return null;

                                const { type, enum: enumOptions } = config;

                                const label = formatLabel(key); // Main field label

                                // const defaultValue = config.default ?? null;

                                const formattedEnumOptions = enumOptions
                                  ? enumOptions.map((option) => ({
                                      value: option,
                                      displayLabel:
                                        typeof option === 'string'
                                          ? formatLabel(option)
                                          : option.toString(),
                                    }))
                                  : null;

                                const fieldValue = values.alertParams[key] ?? config.default;

                                // setFieldValue(`alertParams.${key}`, defaultValue)

                                if (type === 'boolean') {
                                  return (
                                    <Grid item xs={12} mt={4} key={key}>
                                      <CustomToggleSwitch
                                        label={label}
                                        title=""
                                        checked={!!fieldValue}
                                        onChange={(e) =>
                                          setFieldValue(`alertParams.${key}`, e.target.checked)
                                        }
                                      />
                                    </Grid>
                                  );
                                }

                                if (type === 'string' && key === 'pms_sync') {
                                  return (
                                    <Grid item xs={12} mt={4} key={key}>
                                      <CustomToggleSwitch
                                        label={label}
                                        title=""
                                        checked={
                                          typeof fieldValue === 'string'
                                            ? Boolean(fieldValue?.toLowerCase())
                                            : fieldValue
                                        }
                                        onChange={(e) =>
                                          setFieldValue(`alertParams.${key}`, e.target.checked)
                                        }
                                      />
                                    </Grid>
                                  );
                                }

                                // if (
                                //   formattedEnumOptions &&
                                //   formattedEnumOptions.length > 0 &&
                                //   formattedEnumOptions.length <= 3
                                // ) {
                                //   return (
                                //     <Grid item xs={12} mt={4} key={key}>
                                //       <FormControl component="fieldset" fullWidth>
                                //         <Typography variant="subtitle2" gutterBottom>
                                //           {label}
                                //         </Typography>
                                //         <RadioGroup
                                //           row
                                //           name={`alertParams.${key}`}
                                //           value={fieldValue}
                                //           onChange={(e) =>
                                //             setFieldValue(`alertParams.${key}`, e.target.value)
                                //           }
                                //         >
                                //           {formattedEnumOptions.map(({ value, displayLabel }) => (
                                //             <FormControlLabel
                                //               key={value.toString()}
                                //               value={value.toString()}
                                //               control={<Radio size="small" />}
                                //               label={displayLabel}
                                //             />
                                //           ))}
                                //         </RadioGroup>
                                //       </FormControl>
                                //     </Grid>
                                //   );
                                // }

                                if (formattedEnumOptions) {
                                  return (
                                    <Grid item xs={12} mt={4} key={key}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>{label}</InputLabel>
                                        <Select
                                          name={`alertParams.${key}`}
                                          value={fieldValue}
                                          label={label}
                                          onChange={(e) =>
                                            setFieldValue(`alertParams.${key}`, e.target.value)
                                          }
                                        >
                                          {formattedEnumOptions.map(({ value, displayLabel }) => (
                                            <MenuItem
                                              key={value.toString()}
                                              value={value.toString()}
                                            >
                                              {displayLabel}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                  );
                                }

                                if (type === 'integer' || type === 'number') {
                                  return (
                                    <Grid item xs={12} mt={4} key={key}>
                                      <TextField
                                        fullWidth
                                        type="number"
                                        name={`alertParams.${key}`}
                                        label={label}
                                        size="small"
                                        value={fieldValue}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                      />
                                    </Grid>
                                  );
                                }

                                return (
                                  <Grid item xs={12} mt={4} key={key}>
                                    <TextField
                                      fullWidth
                                      name={`alertParams.${key}`}
                                      label={label}
                                      size="small"
                                      value={fieldValue}
                                      onChange={handleChange}
                                      InputLabelProps={{ shrink: true }}
                                    />
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        </Collapse>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" justifyContent="flex-end" mt={6}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      disabled={isSubmitting || values.alertName === ''}
                    >
                      SAVE
                    </Button>
                  </Grid>
                </form>
              );
            }}
          </Formik>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigureAlert;
